import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// RESERVIERUNGSKETTE-001 (Owner 14.10.2026): „Wenn eine Reservierung kommt, muss der Admin das
// sehen.“ Bisher gab es dafür weder Zähler noch Ton — der Reiter „Reservierungen“ lud nur auf
// Knopfdruck. Dieser Provider fragt alle 20 s die Kopfzahlen ab (GET /admin/reservation-inbox/
// summary) und meldet jede NEUE wartende bzw. dringende Reservierung mit Hinweis und kurzem Ton.
// Der Zähler am Menüpunkt „Reservierungen“ zeigt, wie viele gerade Aufmerksamkeit brauchen
// (RESERVIERUNG-KOMPLETT-001: needs_admin = Kachel „Braucht Sie jetzt“).
// ─────────────────────────────────────────────────────────────────────────────

const EMPTY = { pending: 0, urgent: 0, attention: 0, needs_admin: null, open_market: 0, open_market_after_min: 30 };
const ReservationAlertContext = createContext({ summary: EMPTY, refresh: () => {} });
export const useReservationAlert = () => useContext(ReservationAlertContext);

export const POLL_MS = 20000;

// Kurzer Hinweiston ohne Audiodatei. Browser ohne Freigabe (Autoplay) bleiben einfach stumm.
export function playAlertTone() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    [0, 0.22].forEach((offset) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.15, ctx.currentTime + offset);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.18);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(ctx.currentTime + offset); osc.stop(ctx.currentTime + offset + 0.2);
    });
    setTimeout(() => { try { ctx.close(); } catch (_) { /* egal */ } }, 800);
  } catch (_) { /* ohne Ton weiter */ }
}

// Reine Entscheidung (testbar): wann wird gemeldet?
export function alertFor(previous, next) {
  if (!previous || !next) return null;
  if (next.pending > previous.pending) {
    const n = next.pending - previous.pending;
    return `${n === 1 ? 'Neue Reservierung wartet' : `${n} neue Reservierungen warten`} auf Zuweisung (Menü „Reservierungen“). Ohne Zuweisung vermittelt das System sie nach ${next.open_market_after_min || 30} Min. automatisch weiter (für alle offen, PULSE-Essen an einen Partner).`;
  }
  if (next.urgent > previous.urgent) {
    return 'Reservierung in den nächsten 45 Min. noch ohne Fahrer (Menü „Reservierungen“).';
  }
  return null;
}

export function ReservationAlertProvider({ children }) {
  const [summary, setSummary] = useState(EMPTY);
  const last = useRef(null);
  const refresh = useCallback(() => {
    Promise.resolve()
      .then(() => api.get('/admin/reservation-inbox/summary'))
      .then((r) => {
        const next = { ...EMPTY, ...(r.data || {}) };
        const message = alertFor(last.current, next);
        last.current = next;
        setSummary(next);
        if (message) { toast(message, { icon: '🚖', duration: 12000 }); playAlertTone(); }
      })
      .catch(() => {});   // Komfort — Fehler blockieren die Seite nie
  }, []);
  useEffect(() => {
    refresh();
    const poll = setInterval(refresh, POLL_MS);
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(poll); window.removeEventListener('focus', onFocus); };
  }, [refresh]);
  return (
    <ReservationAlertContext.Provider value={{ summary, refresh }}>
      {children}
    </ReservationAlertContext.Provider>
  );
}
